import { useState, useEffect, useRef, type MouseEvent } from "react";
import { Menu, X, PhoneCall, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import JaikLogo from "@/components/agency/JaikLogo";
import { Link, useLocation } from "@/lib/router-compat";
import { cn } from "@/lib/utils";

const links = [
  { label: "Services", href: "/contractor-websites" },
  { label: "SEO", href: "/contractor-seo" },
  { label: "Pricing", href: "/pricing" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

const serviceAreaLinks = [
  { label: "All service areas", href: "/king-county-contractors" },
  { label: "Seattle", href: "/contractor-websites/seattle" },
  { label: "Bellevue", href: "/contractor-websites/bellevue" },
  { label: "Kent", href: "/contractor-websites/kent" },
  { label: "Renton", href: "/contractor-websites/renton" },
  { label: "Tacoma", href: "/contractor-websites/tacoma" },
  { label: "Everett", href: "/contractor-websites/everett" },
  { label: "Olympia", href: "/contractor-websites/olympia" },
  { label: "Bremerton", href: "/contractor-websites/bremerton" },
  { label: "Bellingham", href: "/contractor-websites/bellingham" },
  { label: "Vancouver", href: "/contractor-websites/vancouver" },
];

const AgencyNavbar = () => {
  const [open, setOpen] = useState(false);
  const [countyOpen, setCountyOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const countyMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setCountyOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeCountyMenu = (event: globalThis.MouseEvent) => {
      if (!countyMenuRef.current?.contains(event.target as Node)) setCountyOpen(false);
    };
    document.addEventListener("pointerdown", closeCountyMenu);
    return () => document.removeEventListener("pointerdown", closeCountyMenu);
  }, []);

  const handleLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);

    if (location.pathname === "/") {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-agency-bg/[0.98] backdrop-blur-xl border-b border-agency-border/50 shadow-lg"
          : "bg-gradient-to-b from-agency-bg/40 via-agency-bg/10 to-transparent backdrop-blur-xs border-b border-transparent"
      )}
    >
      <div className="container flex items-center justify-between h-14 sm:h-16 md:h-20">
        <Link
          to="/"
          onClick={handleLogoClick}
          className="font-heading text-lg sm:text-xl font-bold tracking-tight text-agency-text"
        >
          <JaikLogo />
        </Link>

        <div className="hidden md:flex items-center gap-5 lg:gap-7">
          {links.slice(0, 2).map((l) => (
            <Link
              key={l.label}
              to={l.href}
              className="font-sans text-sm text-agency-text-muted hover:text-agency-text transition-colors focus-visible:outline-hidden focus-visible:text-agency-text"
            >
              {l.label}
            </Link>
          ))}
          <div ref={countyMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setCountyOpen((current) => !current)}
              aria-expanded={countyOpen}
              aria-controls="service-area-desktop-menu"
              className="inline-flex items-center gap-1 whitespace-nowrap font-sans text-sm text-agency-text-muted hover:text-agency-text transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-agency-accent rounded-sm"
            >
              Service Area
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", countyOpen && "rotate-180")} />
            </button>
            <AnimatePresence>
              {countyOpen && (
                <motion.div
                  id="service-area-desktop-menu"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  className="absolute left-1/2 top-full mt-4 w-64 -translate-x-1/2 border border-agency-border bg-agency-bg/[0.98] p-2 shadow-xl backdrop-blur-xl"
                >
                  {serviceAreaLinks.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-3 py-2.5 font-sans text-sm text-agency-text-muted hover:bg-agency-surface hover:text-agency-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-agency-accent"
                    >
                      {item.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {links.slice(2).map((l) => (
            <Link
              key={l.label}
              to={l.href}
              className="font-sans text-sm text-agency-text-muted hover:text-agency-text transition-colors focus-visible:outline-hidden focus-visible:text-agency-text"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/book"
            className="btn-jaik btn-jaik-primary"
          >
            <PhoneCall className="h-3.5 w-3.5" />
            Book a call
          </Link>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <Link
            to="/book"
            className="btn-jaik btn-jaik-primary btn-jaik-sm"
          >
            <PhoneCall className="h-3 w-3" />
            Book a call
          </Link>
          <button className="text-agency-text p-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-agency-accent rounded" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden max-h-[calc(100svh-3.5rem)] overflow-y-auto bg-agency-bg/[0.98] backdrop-blur-xl border-t border-agency-border"
          >
            <div className="px-6 py-5 space-y-1">
              {links.slice(0, 2).map((l, i) => (
                <motion.div key={l.label} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="block font-sans text-agency-text py-3 text-lg border-b border-agency-border/30 last:border-0"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <div className="border-b border-agency-border/30">
                <button
                  type="button"
                  onClick={() => setCountyOpen((current) => !current)}
                  aria-expanded={countyOpen}
                  aria-controls="service-area-mobile-menu"
                  className="flex w-full items-center justify-between py-3 font-sans text-lg text-agency-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-agency-accent"
                >
                  Service Area
                  <ChevronDown className={cn("h-4 w-4 transition-transform", countyOpen && "rotate-180")} />
                </button>
                <AnimatePresence initial={false}>
                  {countyOpen && (
                    <motion.div
                      id="service-area-mobile-menu"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden pb-2"
                    >
                      {serviceAreaLinks.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setOpen(false)}
                          className="block py-2 pl-4 font-sans text-sm text-agency-text-muted hover:text-agency-text"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {links.slice(2).map((l, i) => (
                <motion.div key={l.label} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: (i + 3) * 0.05 }}>
                  <Link
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="block font-sans text-agency-text py-3 text-lg border-b border-agency-border/30 last:border-0"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <div className="flex gap-2 mt-4">
                <Link
                  to="/book"
                  onClick={() => setOpen(false)}
                  className="btn-jaik btn-jaik-primary flex-1"
                >
                  Book a call
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  className="btn-jaik btn-jaik-secondary flex-1"
                >
                  Free mockup
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default AgencyNavbar;
